import { create } from 'zustand'
import type {
  GameStatus,
  TowerRoll,
  BonusType,
  GameHistory,
} from '@/types'
import { GAME_CONFIG } from '@/types'
import { rollTypeToVariant } from '@/types/rollMapping'
import { useSessionStore } from './sessionStore'

// Backend uses normalized coordinates (amplitude ~0.42).
// Frontend uses pixels (amplitude ~85px).
// This factor converts between them.
export const PIXELS_PER_UNIT = 202

// ── Server payload types ──

// TS backend sends numbers directly (not Decimal strings like Python did)
export interface ServerSessionPayload {
  session_id: string
  player_id?: string
  currency?: string
  phase: string             // TS-бек: "phase", не "state"
  balance: number
  bet_amount: number
  current_multiplier: number
  current_potential_win: number
  tower_height: number
  is_hot_rolls_active: boolean
  hot_rolls_ends_at: number | null
  hot_rolls_ms_left?: number | null
  current_cycle_id: string | null
  current_roll_type: string | null
  current_roll_width: number | null
  current_cycle_period_ms: number | null
  current_cycle_amplitude: number | null
  current_cycle_phase_shift_ms: number | null
  current_cycle_origin_x: number | null
  current_cycle_started_at: number | null  // epoch ms (not ISO string)
  top_roll_width: number | null
  next_bonus_line_height: number
  stability_bonus_active: boolean
  hot_rolls_chance_bonus: number
  purchased_bonus_ids?: string[]
}

export interface ServerRollResultPayload {
  round_index: number
  coefficient: number
  coefficient_final: number
  outcome: string           // "success" | "miss" | "edge_fail"
  placement_outcome: string
  roll_type: string
  is_hot_roll: boolean
  bonus_line_hit: boolean
  potential_win: number
  cycle_id: string
  release_ms: number
  roll_width: number
  landing_center_offset: number
  overlap_ratio: number
  is_edge_placement: boolean
  session: ServerSessionPayload
}

// ── Store interface ──

interface GameStore {
  // === State ===
  status: GameStatus
  balance: number
  betAmount: number
  lastBetAmount: number
  currentStake: number
  currentMultiplier: number
  tower: TowerRoll[]
  isHotRollsActive: boolean
  hotRollsEndTime: number | null
  activeBonus: BonusType | null
  lastCoefficient: number | null
  gameHistory: GameHistory[]
  hotRollsChanceBonus: number
  hasStability: boolean
  purchasedBonusIds: string[]
  towerHeight: number
  currentWin: number

  // === Server cycle params (for Chopsticks sync) ===
  wsSessionId: string | null
  currentCycleId: string | null
  cyclePeriodMs: number
  cycleAmplitude: number     // in normalized units (backend space)
  cyclePhaseShiftMs: number
  cycleOriginX: number       // in normalized units
  cycleStartedAt: string | null
  // Client-local time (Date.now()) when this cycle was received.
  // Used to compute release_ms without clock-skew between client and server.
  cycleStartedAtClientTime: number
  currentRollType: string | null
  currentRollWidth: number   // in normalized units
  nextBonusLineHeight: number

  // === Actions (UI) ===
  setBalance: (balance: number) => void
  setBetAmount: (amount: number) => void
  resetGame: () => void

  // === Server-authoritative actions ===
  // arrivedAt: client Date.now() captured at message receive time. Lets the
  // store anchor `hot_rolls_ms_left` to receive time, not deferred-apply time
  // (which is shifted by the falling-roll animation).
  applyServerState: (payload: ServerSessionPayload, arrivedAt?: number) => void
  applyRollResult: (payload: ServerRollResultPayload, arrivedAt?: number) => void
  applyGameOver: (payload: ServerSessionPayload, arrivedAt?: number) => void
  applyCashOut: (payload: ServerSessionPayload, arrivedAt?: number) => void

  // === Demo mode fallback ===
  startGame: () => void
  addRoll: (roll: Omit<TowerRoll, 'id'>) => void
  updateStake: (coefficient: number) => boolean
  activateHotRolls: () => void
  deactivateHotRolls: () => void
  purchaseBonus: (type: BonusType, cost: number) => boolean
  checkHotRollsTrigger: () => boolean
  getHotRollsChance: () => number
  useStability: () => void
  cashOut: () => number
  gameOver: (reason: 'miss' | 'edge') => void
  addToHistory: (entry: GameHistory) => void
}

// ── Helpers ──

const BASE_HOT_ROLLS_CHANCE = 5

function serverStateToStatus(state: string): GameStatus {
  switch (state) {
    case 'idle': return 'idle'
    case 'awaiting_drop': return 'playing'
    case 'hot_rolls_active': return 'hot_rolls'
    case 'game_over': return 'game_over'
    case 'cashed_out': return 'cashed_out'
    default: return 'playing'
  }
}

function syncBalance(amount: number) {
  const sessionStore = useSessionStore.getState()
  if (sessionStore.isDemoMode) {
    sessionStore.setDemoBalance(amount)
  } else {
    setTimeout(() => sessionStore.refreshBalance(), 300)
  }
}

// ── Store ──

export const useGameStore = create<GameStore>((set, get) => ({
  // === Initial state ===
  status: 'idle',
  balance: 0,
  betAmount: 0,
  lastBetAmount: 0,
  currentStake: 0,
  currentMultiplier: 1,
  tower: [],
  isHotRollsActive: false,
  hotRollsEndTime: null,
  activeBonus: null,
  lastCoefficient: null,
  gameHistory: [],
  hotRollsChanceBonus: 0,
  hasStability: false,
  purchasedBonusIds: [],

  // Cycle params
  wsSessionId: null,
  currentCycleId: null,
  cyclePeriodMs: 1800,
  cycleAmplitude: 0.42,
  cyclePhaseShiftMs: 0,
  cycleOriginX: 0,
  cycleStartedAt: null,
  cycleStartedAtClientTime: 0,
  currentRollType: null,
  currentRollWidth: 0.32,
  nextBonusLineHeight: 10,

  get towerHeight() { return get().tower.length },
  get currentWin() { return Math.floor(get().currentStake * 100) / 100 },

  // ──────────────────────────────────────────
  // SERVER-AUTHORITATIVE ACTIONS
  // ──────────────────────────────────────────

  applyServerState: (payload, arrivedAt = Date.now()) => {
    const status = serverStateToStatus(payload.phase)
    const balance = payload.balance
    const prev = get()
    const sameCycle = !!prev.currentCycleId && prev.currentCycleId === payload.current_cycle_id
    const { minBet, maxBet, defaultBet } = useSessionStore.getState()
    const preservedBet = prev.betAmount > 0 ? prev.betAmount : defaultBet
    const idleBetAmount = payload.bet_amount > 0
      ? payload.bet_amount
      : Math.min(Math.max(preservedBet, minBet), Math.min(balance, maxBet, GAME_CONFIG.MAX_BET))

    set({
      wsSessionId: payload.session_id,
      status,
      balance,
      currentStake: payload.current_potential_win,
      currentMultiplier: payload.current_multiplier,
      isHotRollsActive: payload.is_hot_rolls_active,
      // Use server-supplied ms_left for clock-skew-free timer, anchored to
      // the moment the message arrived (not when this setter ran — the caller
      // may have deferred application by the falling-roll animation).
      hotRollsEndTime: typeof payload.hot_rolls_ms_left === 'number' && payload.hot_rolls_ms_left > 0
        ? arrivedAt + payload.hot_rolls_ms_left
        : (payload.hot_rolls_ends_at ?? null),
      hasStability: payload.stability_bonus_active,
      hotRollsChanceBonus: payload.hot_rolls_chance_bonus * 100,
      purchasedBonusIds: payload.purchased_bonus_ids ?? [],
      betAmount: idleBetAmount,
      lastBetAmount: payload.bet_amount,
      nextBonusLineHeight: payload.next_bonus_line_height ?? 10,

      // Cycle params — TS backend sends epoch ms, not ISO string
      currentCycleId: payload.current_cycle_id,
      cyclePeriodMs: payload.current_cycle_period_ms ?? 1800,
      cycleAmplitude: payload.current_cycle_amplitude ?? 0.42,
      cyclePhaseShiftMs: payload.current_cycle_phase_shift_ms ?? 0,
      cycleOriginX: payload.current_cycle_origin_x ?? 0,
      cycleStartedAt: payload.current_cycle_started_at
        ? new Date(payload.current_cycle_started_at).toISOString()
        : null,
      // Preserve client-time baseline if the cycle ID didn't change. Avoids
      // restarting Chopsticks animation phase when the server simply
      // re-sends the same cycle (e.g. after BET IN with reused cycle).
      cycleStartedAtClientTime: sameCycle
        ? prev.cycleStartedAtClientTime
        : (payload.current_cycle_started_at ? Date.now() : 0),
      currentRollType: payload.current_roll_type,
      currentRollWidth: payload.current_roll_width ?? 0.32,
    })

    syncBalance(balance)
  },

  applyRollResult: (payload, arrivedAt = Date.now()) => {
    const session = payload.session
    const coefficient = payload.coefficient_final
    const variant = rollTypeToVariant(payload.roll_type)
    const isHot = payload.is_hot_roll
    const offsetPx = payload.landing_center_offset * PIXELS_PER_UNIT
    const previousRoll = get().tower[get().tower.length - 1]
    const previousX = previousRoll ? previousRoll.positionX : 0
    const positionXPx = previousX + offsetPx

    // Add roll to tower
    const newRoll: TowerRoll = {
      id: `roll-${payload.cycle_id}-${payload.round_index}`,
      variant,
      type: isHot ? 'hot' : 'normal',
      positionX: positionXPx,
      coefficient,
      isBonus: payload.bonus_line_hit,
    }

    const balance = session.balance
    const status = serverStateToStatus(session.phase)

    set((state) => {
      // Anti-stale guard: this setter is invoked AFTER a ~750ms animation
      // defer. If a fresher game_state arrived in the meantime (typical
      // case: server-driven HOT ROLLS expiry pushed a new normal cycle),
      // store.currentCycleId no longer matches the cycle the player just
      // dropped against (`payload.cycle_id`). In that case the cycle params
      // attached to this roll_result are already stale — overwriting would
      // pin the client to a retired cycle and break every subsequent drop
      // with `cycle mismatch`. Skip cycle/hot-rolls fields, keep the
      // tower/balance/status updates that this roll legitimately produced.
      const cycleStateStillFresh = state.currentCycleId === payload.cycle_id

      const base = {
        tower: [...state.tower, newRoll],
        lastCoefficient: coefficient,
        status,
        balance,
        currentStake: session.current_potential_win,
        currentMultiplier: session.current_multiplier,
        nextBonusLineHeight: session.next_bonus_line_height ?? 10,
        hasStability: session.stability_bonus_active,
        hotRollsChanceBonus: session.hot_rolls_chance_bonus * 100,
        purchasedBonusIds: session.purchased_bonus_ids ?? [],
      }

      if (!cycleStateStillFresh) return base

      return {
        ...base,
        isHotRollsActive: session.is_hot_rolls_active,
        // Anchor HOT ROLLS deadline to the moment the WS message arrived,
        // NOT Date.now(): MainScreen defers applyRollResult by the falling
        // roll animation (~750ms), and Date.now() would push the banner
        // deadline past the server's expiry timer.
        hotRollsEndTime: typeof session.hot_rolls_ms_left === 'number' && session.hot_rolls_ms_left > 0
          ? arrivedAt + session.hot_rolls_ms_left
          : (session.hot_rolls_ends_at ?? null),
        currentCycleId: session.current_cycle_id,
        cyclePeriodMs: session.current_cycle_period_ms ?? 1800,
        cycleAmplitude: session.current_cycle_amplitude ?? 0.42,
        cyclePhaseShiftMs: session.current_cycle_phase_shift_ms ?? 0,
        cycleOriginX: session.current_cycle_origin_x ?? 0,
        cycleStartedAt: session.current_cycle_started_at
          ? new Date(session.current_cycle_started_at).toISOString()
          : null,
        cycleStartedAtClientTime: session.current_cycle_started_at ? Date.now() : 0,
        currentRollType: session.current_roll_type,
        currentRollWidth: session.current_roll_width ?? 0.32,
      }
    })

    syncBalance(balance)
  },

  applyGameOver: (payload, _arrivedAt = Date.now()) => {
    void _arrivedAt
    const balance = payload.balance
    const { betAmount, tower, currentMultiplier } = get()

    get().addToHistory({
      gameId: `game-${Date.now()}`,
      date: new Date(),
      bet: betAmount,
      result: 0,
      rollsCount: tower.length,
      maxCoefficient: currentMultiplier,
      status: 'loss',
    })

    // Sync cycle params from server's pre-spawned idle cycle so that the
    // next BET IN reuses the same cycle (sameCycle=true), keeping
    // Chopsticks animation continuous.
    const prev = get()
    const sameCycle = !!prev.currentCycleId && prev.currentCycleId === payload.current_cycle_id
    set({
      status: 'game_over',
      balance,
      currentStake: 0,
      currentCycleId: payload.current_cycle_id,
      cyclePeriodMs: payload.current_cycle_period_ms ?? prev.cyclePeriodMs,
      cycleAmplitude: payload.current_cycle_amplitude ?? prev.cycleAmplitude,
      cyclePhaseShiftMs: payload.current_cycle_phase_shift_ms ?? prev.cyclePhaseShiftMs,
      cycleOriginX: payload.current_cycle_origin_x ?? prev.cycleOriginX,
      cycleStartedAt: payload.current_cycle_started_at
        ? new Date(payload.current_cycle_started_at).toISOString()
        : prev.cycleStartedAt,
      cycleStartedAtClientTime: sameCycle
        ? prev.cycleStartedAtClientTime
        : (payload.current_cycle_started_at ? Date.now() : prev.cycleStartedAtClientTime),
      currentRollType: payload.current_roll_type,
      currentRollWidth: payload.current_roll_width ?? prev.currentRollWidth,
    })

    syncBalance(balance)
  },

  applyCashOut: (payload, _arrivedAt = Date.now()) => {
    void _arrivedAt
    const balance = payload.balance
    const { betAmount, tower, currentMultiplier, currentStake } = get()

    get().addToHistory({
      gameId: `game-${Date.now()}`,
      date: new Date(),
      bet: betAmount,
      result: currentStake,
      rollsCount: tower.length,
      maxCoefficient: currentMultiplier,
      status: 'win',
    })

    // Same cycle sync as applyGameOver — keep BET IN smooth.
    const prev = get()
    const sameCycle = !!prev.currentCycleId && prev.currentCycleId === payload.current_cycle_id
    set({
      status: 'cashed_out',
      balance,
      currentCycleId: payload.current_cycle_id,
      cyclePeriodMs: payload.current_cycle_period_ms ?? prev.cyclePeriodMs,
      cycleAmplitude: payload.current_cycle_amplitude ?? prev.cycleAmplitude,
      cyclePhaseShiftMs: payload.current_cycle_phase_shift_ms ?? prev.cyclePhaseShiftMs,
      cycleOriginX: payload.current_cycle_origin_x ?? prev.cycleOriginX,
      cycleStartedAt: payload.current_cycle_started_at
        ? new Date(payload.current_cycle_started_at).toISOString()
        : prev.cycleStartedAt,
      cycleStartedAtClientTime: sameCycle
        ? prev.cycleStartedAtClientTime
        : (payload.current_cycle_started_at ? Date.now() : prev.cycleStartedAtClientTime),
      currentRollType: payload.current_roll_type,
      currentRollWidth: payload.current_roll_width ?? prev.currentRollWidth,
    })

    syncBalance(balance)
  },

  // ──────────────────────────────────────────
  // UI ACTIONS
  // ──────────────────────────────────────────

  setBalance: (balance) => set((state) => {
    if (state.betAmount > 0) return { balance }

    const { minBet, maxBet: sessionMaxBet, defaultBet } = useSessionStore.getState()
    const maxBet = Math.min(balance, sessionMaxBet, GAME_CONFIG.MAX_BET)
    const betAmount = maxBet > 0 ? Math.min(Math.max(defaultBet, minBet), maxBet) : 0

    return { balance, betAmount }
  }),

  setBetAmount: (amount) => {
    const clampedAmount = Math.max(0, Math.min(amount, get().balance, useSessionStore.getState().maxBet, GAME_CONFIG.MAX_BET))
    set({ betAmount: clampedAmount })
  },

  resetGame: () => {
    // Preserve cycle params — server already pre-spawned a fresh idle cycle
    // when the previous round ended. Resetting them locally would cause
    // Chopsticks to restart its animation phase when the player BETs again.
    set({
      status: 'idle',
      currentStake: 0,
      currentMultiplier: 1,
      tower: [],
      isHotRollsActive: false,
      hotRollsEndTime: null,
      activeBonus: null,
      lastCoefficient: null,
      hotRollsChanceBonus: 0,
      hasStability: false,
      purchasedBonusIds: [],
    })
  },

  addToHistory: (entry) => {
    set((state) => ({
      gameHistory: [entry, ...state.gameHistory].slice(0, 50),
    }))
  },

  // ──────────────────────────────────────────
  // DEMO MODE FALLBACK (local game logic)
  // Used only when no WS connection is available
  // ──────────────────────────────────────────

  startGame: () => {
    const { betAmount, balance } = get()
    if (betAmount <= 0 || betAmount > balance) return
    const newBalance = balance - betAmount
    set({
      status: 'playing',
      balance: newBalance,
      lastBetAmount: betAmount,
      currentStake: betAmount,
      currentMultiplier: 1,
      tower: [],
      activeBonus: null,
      lastCoefficient: null,
      hotRollsChanceBonus: 0,
      hasStability: false,
      purchasedBonusIds: [],
      isHotRollsActive: false,
      hotRollsEndTime: null,
    })
    syncBalance(newBalance)
  },

  addRoll: (roll) => {
    const newRoll: TowerRoll = {
      ...roll,
      id: `roll-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    }
    set((state) => ({
      tower: [...state.tower, newRoll],
      lastCoefficient: roll.coefficient,
    }))
  },

  updateStake: (coefficient) => {
    const { currentStake } = get()
    const newStake = Math.round(currentStake * coefficient * 100) / 100
    set({
      currentStake: newStake,
      currentMultiplier: get().currentMultiplier * coefficient,
    })
    return newStake < 0.01
  },

  activateHotRolls: () => {
    set({
      isHotRollsActive: true,
      hotRollsEndTime: Date.now() + GAME_CONFIG.HOT_ROLLS_DURATION,
      status: 'hot_rolls',
    })
    setTimeout(() => {
      if (get().isHotRollsActive) get().deactivateHotRolls()
    }, GAME_CONFIG.HOT_ROLLS_DURATION)
  },

  deactivateHotRolls: () => {
    set({ isHotRollsActive: false, hotRollsEndTime: null, status: 'playing' })
  },

  purchaseBonus: (type, cost) => {
    const { currentStake, hotRollsChanceBonus, hasStability, purchasedBonusIds } = get()
    const newStake = currentStake - cost
    if (newStake <= 0) return false
    const bonusIdMap: Record<BonusType, string> = {
      hot_chance_10: 'hot_rolls_10',
      hot_chance_30: 'hot_rolls_30',
      hot_chance_50: 'hot_rolls_50',
      stability: 'stability',
    }
    const bonusId = bonusIdMap[type]
    if (purchasedBonusIds.includes(bonusId)) return false
    if (type.startsWith('hot_chance_') && purchasedBonusIds.some(id => id.startsWith('hot_rolls_'))) return false
    const nextPurchasedBonusIds = [...purchasedBonusIds, bonusId]
    if (type === 'hot_chance_10') set({ currentStake: newStake, hotRollsChanceBonus: Math.min(hotRollsChanceBonus + 10, 95), purchasedBonusIds: nextPurchasedBonusIds })
    else if (type === 'hot_chance_30') set({ currentStake: newStake, hotRollsChanceBonus: Math.min(hotRollsChanceBonus + 30, 95), purchasedBonusIds: nextPurchasedBonusIds })
    else if (type === 'hot_chance_50') set({ currentStake: newStake, hotRollsChanceBonus: Math.min(hotRollsChanceBonus + 50, 95), purchasedBonusIds: nextPurchasedBonusIds })
    else if (type === 'stability') {
      if (hasStability) return false
      set({ currentStake: newStake, hasStability: true, activeBonus: type, purchasedBonusIds: nextPurchasedBonusIds })
    }
    return true
  },

  checkHotRollsTrigger: () => {
    const { isHotRollsActive, hotRollsChanceBonus, status } = get()
    if (isHotRollsActive || status !== 'playing') return false
    const totalChance = BASE_HOT_ROLLS_CHANCE + hotRollsChanceBonus
    if (Math.random() * 100 < totalChance) {
      set({ isHotRollsActive: true, hotRollsEndTime: Date.now() + GAME_CONFIG.HOT_ROLLS_DURATION, status: 'hot_rolls' })
      setTimeout(() => {
        const s = get()
        if (s.isHotRollsActive && s.status === 'hot_rolls')
          set({ isHotRollsActive: false, hotRollsEndTime: null, status: 'playing' })
      }, GAME_CONFIG.HOT_ROLLS_DURATION)
      return true
    }
    return false
  },

  getHotRollsChance: () => BASE_HOT_ROLLS_CHANCE + get().hotRollsChanceBonus,

  useStability: () => set({ hasStability: false, activeBonus: null }),

  cashOut: () => {
    const { currentStake, balance, betAmount, tower, currentMultiplier } = get()
    const newBalance = balance + currentStake
    get().addToHistory({
      gameId: `game-${Date.now()}`, date: new Date(), bet: betAmount,
      result: currentStake, rollsCount: tower.length, maxCoefficient: currentMultiplier, status: 'win',
    })
    set({ status: 'cashed_out', balance: newBalance })
    syncBalance(newBalance)
    return currentStake
  },

  gameOver: (reason) => {
    const { hasStability, betAmount, tower, currentMultiplier } = get()
    if (reason === 'edge' && hasStability) {
      const safeCoef = 0.1 + Math.random() * 0.4
      get().updateStake(safeCoef)
      return
    }
    get().addToHistory({
      gameId: `game-${Date.now()}`, date: new Date(), bet: betAmount,
      result: 0, rollsCount: tower.length, maxCoefficient: currentMultiplier, status: 'loss',
    })
    set({ status: 'game_over', currentStake: 0 })
  },
}))

// ── Selectors ──

export const selectTowerHeight = (state: GameStore) => state.tower.length
export const selectIsPlaying = (state: GameStore) =>
  state.status === 'playing' || state.status === 'hot_rolls'
export const selectCanBet = (state: GameStore) =>
  state.status === 'idle' && state.betAmount <= state.balance
export const selectIsBonusRoll = (state: GameStore) =>
  (state.tower.length + 1) % GAME_CONFIG.BONUS_LINE_INTERVAL === 0
export const selectNextBonusIn = (state: GameStore) =>
  GAME_CONFIG.BONUS_LINE_INTERVAL - (state.tower.length % GAME_CONFIG.BONUS_LINE_INTERVAL)

export default useGameStore
